/**
 * Loyalty API (active tenant) — backed by the BFF (`/api/v1/loyalty/**`), which
 * orchestrates web-loyalty-service. The session tenant flows downstream as
 * `X-Tenant-Id`; the browser holds no token (OAuth2-BFF cookie session), so these
 * calls just use `apiClient` (CSRF + cookie are attached by its interceptor).
 *
 * Writes carry an `Idempotency-Key` header — the BFF relays it to web-loyalty-service
 * so a retry with the same key does not double-apply.
 */
import type { AxiosRequestConfig } from 'axios';

import { apiClient } from '@/shared/api';

import { newIdempotencyKey } from '../lib/idempotency';
import type {
  BonusAccount,
  BonusReservation,
  BonusTransaction,
  CardPoolOperationResult,
  CardPoolPage,
  CardPoolStatus,
  CustomerDiscountResponse,
  GenerateCardPoolRequest,
  InitializeDefaultsResponse,
  IssueCardRequest,
  LoyaltyCard,
  LoyaltyProgramConfigRequest,
  LoyaltyProgramConfigResponse,
  LoyaltyRule,
  LoyaltyTier,
  PointsMutationRequest,
  ReservePointsRequest,
  TransferCardsRequest,
  UpsertRuleRequest,
  UpsertTierRequest,
} from '../model/types';

const BASE = '/api/v1/loyalty';

/** Axios config carrying a fresh `Idempotency-Key` for a write. */
const idempotent = (): AxiosRequestConfig => ({
  headers: { 'Idempotency-Key': newIdempotencyKey() },
});

// ── Programme administration ──────────────────────────────────────────────────

export const listTiers = async (): Promise<LoyaltyTier[]> =>
  (await apiClient.get<LoyaltyTier[]>(`${BASE}/tiers`)).data;

export const upsertTier = async (body: UpsertTierRequest): Promise<LoyaltyTier> =>
  (await apiClient.post<LoyaltyTier>(`${BASE}/tiers`, body, idempotent())).data;

export const listRules = async (): Promise<LoyaltyRule[]> =>
  (await apiClient.get<LoyaltyRule[]>(`${BASE}/rules`)).data;

export const upsertRule = async (body: UpsertRuleRequest): Promise<LoyaltyRule> =>
  (await apiClient.post<LoyaltyRule>(`${BASE}/rules`, body, idempotent())).data;

export const initializeDefaults = async (): Promise<InitializeDefaultsResponse> =>
  (await apiClient.post<InitializeDefaultsResponse>(`${BASE}/admin/initialize-defaults`)).data;

// ── Per-customer data + operations ────────────────────────────────────────────

export const getBonusAccount = async (customerId: string): Promise<BonusAccount> =>
  (await apiClient.get<BonusAccount>(`${BASE}/customers/${customerId}/bonus-account`)).data;

export const getBonusTransactions = async (customerId: string): Promise<BonusTransaction[]> =>
  (await apiClient.get<BonusTransaction[]>(`${BASE}/customers/${customerId}/bonus-account/transactions`))
    .data;

export const getCustomerTier = async (customerId: string): Promise<LoyaltyTier> =>
  (await apiClient.get<LoyaltyTier>(`${BASE}/customers/${customerId}/tier`)).data;

export const getCustomerCards = async (customerId: string): Promise<LoyaltyCard[]> =>
  (await apiClient.get<LoyaltyCard[]>(`${BASE}/customers/${customerId}/cards`)).data;

export const getCardByNumber = async (cardNumber: string): Promise<LoyaltyCard> =>
  (await apiClient.get<LoyaltyCard>(`${BASE}/cards/${encodeURIComponent(cardNumber)}`)).data;

export const accruePoints = async (
  customerId: string,
  body: PointsMutationRequest,
): Promise<BonusTransaction> =>
  (await apiClient.post<BonusTransaction>(
    `${BASE}/customers/${customerId}/bonus-account/accrue`,
    body,
    idempotent(),
  )).data;

export const redeemPoints = async (
  customerId: string,
  body: PointsMutationRequest,
): Promise<BonusTransaction> =>
  (await apiClient.post<BonusTransaction>(
    `${BASE}/customers/${customerId}/bonus-account/redeem`,
    body,
    idempotent(),
  )).data;

export const issueCard = async (
  customerId: string,
  body: IssueCardRequest,
): Promise<LoyaltyCard> =>
  (await apiClient.post<LoyaltyCard>(`${BASE}/customers/${customerId}/cards`, body, idempotent()))
    .data;

// ── Card lifecycle (deactivate / transfer) ──────────────────────────────────────

export const deactivateCard = async (cardNumber: string): Promise<LoyaltyCard> =>
  (await apiClient.post<LoyaltyCard>(`${BASE}/cards/${encodeURIComponent(cardNumber)}/deactivate`))
    .data;

export const transferCards = async (body: TransferCardsRequest): Promise<LoyaltyCard[]> =>
  (await apiClient.post<LoyaltyCard[]>(`${BASE}/cards/transfer`, body)).data;

export const getCustomerDiscount = async (customerId: string): Promise<CustomerDiscountResponse> =>
  (await apiClient.get<CustomerDiscountResponse>(`${BASE}/customers/${customerId}/discount`)).data;

// ── Free-card pool (list / import / generate) ───────────────────────────────────

export const listCardPool = async (params: {
  status?: CardPoolStatus;
  page?: number;
  size?: number;
}): Promise<CardPoolPage> =>
  (await apiClient.get<CardPoolPage>(`${BASE}/cards/pool`, { params })).data;

export const importCardPool = async (file: File): Promise<CardPoolOperationResult> => {
  const form = new FormData();
  form.append('file', file);
  return (
    await apiClient.post<CardPoolOperationResult>(`${BASE}/cards/pool/import`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
  ).data;
};

export const generateCardPool = async (
  body: GenerateCardPoolRequest,
): Promise<CardPoolOperationResult> =>
  (await apiClient.post<CardPoolOperationResult>(`${BASE}/cards/pool/generate`, body)).data;

// ── Programme model (BONUS / DISCOUNT / NONE) ───────────────────────────────────

export const getProgramConfig = async (): Promise<LoyaltyProgramConfigResponse> =>
  (await apiClient.get<LoyaltyProgramConfigResponse>(`${BASE}/program/config`)).data;

export const updateProgramConfig = async (
  body: LoyaltyProgramConfigRequest,
): Promise<LoyaltyProgramConfigResponse> =>
  (await apiClient.put<LoyaltyProgramConfigResponse>(`${BASE}/program/config`, body)).data;

// ── Bonus reservations (two-phase holds: reserve → confirm / release) ────────────
// No Idempotency-Key — the BFF/downstream is idempotent on the reservation's referenceId.

export const reservePoints = async (
  customerId: string,
  body: ReservePointsRequest,
): Promise<BonusReservation> =>
  (await apiClient.post<BonusReservation>(
    `${BASE}/customers/${customerId}/bonus-account/reserve`,
    body,
  )).data;

export const getReservation = async (referenceId: string): Promise<BonusReservation> =>
  (await apiClient.get<BonusReservation>(`${BASE}/reservations/${encodeURIComponent(referenceId)}`))
    .data;

export const confirmReservation = async (referenceId: string): Promise<BonusReservation> =>
  (await apiClient.post<BonusReservation>(
    `${BASE}/reservations/${encodeURIComponent(referenceId)}/confirm`,
  )).data;

export const releaseReservation = async (referenceId: string): Promise<BonusReservation> =>
  (await apiClient.post<BonusReservation>(
    `${BASE}/reservations/${encodeURIComponent(referenceId)}/release`,
  )).data;
